import { camelizeKeys } from "humps";
import store from "@/store/index";
import ls from "local-storage";
import axios from "axios";
import _ from "lodash";
import jwtDecode from "jwt-decode";
import { getCurrentPractice } from "../helpers/current_practice";

import PracticesService from "@/services/PracticesService";
import BaseService from "./BaseService";

export default {
  isLoggedIn() {
    const user = ls(process.env.VUE_APP_LS_USER);
    return new Promise((resolve, reject) => {
      if (user && user.isAuth) {
        store.dispatch("loggedIn");
        resolve();
      } else {
        store.dispatch("loggedOut");
        reject();
      }
    });
  },

  async postLoginSetup(data) {
    ls(process.env.VUE_APP_LS_USER, { isAuth: true, jwt: data.data });
    await this.setPermissions();
    store.dispatch("loggedIn");
    await this.getUser();
  },

  // Send a request to the login URL and save the returned JWT
  async login(creds) {
    const authData = {
      grant_type: "password",
      type: "email",
      username: creds.email.toLowerCase(),
      password: creds.password
    };

    try {
      const data = await axios.post(
        `${BaseService.apiUrl()}/access/token`,
        authData
      );
      await this.postLoginSetup(data);

      return Promise.resolve();
    } catch (e) {
      return Promise.reject();
    }
  },

  refreshTokenRequest: null,

  resetRefreshTokenRequest() {
    this.refreshTokenRequest = null;
  },

  refreshToken() {
    if (!this.refreshTokenRequest) {
      const user = ls(process.env.VUE_APP_LS_USER);
      if (_.has(user, "jwt")) {
        const authData = {
          grant_type: "refresh_token",
          refresh_token: user.jwt.refresh_token
        };

        this.refreshTokenRequest = axios.post(
          `${BaseService.apiUrl()}/access/token`,
          authData
        );
        this.refreshTokenRequest
          .then(data => {
            ls(process.env.VUE_APP_LS_USER, { isAuth: true, jwt: data.data });
            store.dispatch("loggedIn");
            this.getUser();
            return Promise.resolve();
          })
          .catch(Promise.reject());
      }
    }

    return this.refreshTokenRequest;
  },

  async getUser() {
    this.setPermissions();
    if (BaseService.isUser()) {
      window.location = "/#/encounter";
    }
    const response = await BaseService.get("user/current");
    store.dispatch("setUser", camelizeKeys(response.data.data));
    const practices = await PracticesService.list();
    store.dispatch("practice/switch", practices.first);
    BaseService.setSubdomain(practices.subdomain);
    if (practices.count > 1) {
      let practice = getCurrentPractice();
      if (!practice) {
        practice = {
          id: practices.first,
          attributes: {
            subdomain: practices.subdomain
          }
        };
      }
      window.bus.$emit("practice-switch", practice);
    }

    return Promise.resolve();
  },

  setPermissions() {
    const user = ls(process.env.VUE_APP_LS_USER);

    if (user) {
      const token = jwtDecode(user.jwt.access_token);
      ls(process.env.VUE_APP_LS_PERMS, token.permissions);
    }
  },

  forgotPassword(email) {
    const requestData = {
      email
    };

    return axios
      .post(`${BaseService.apiUrl()}/reset-password`, requestData)
      .then(() => Promise.resolve())
      .catch(Promise.reject());
  },

  resetPassword(data, token) {
    const requestData = {
      email: data.email,
      token,
      password: data.password,
      passwordConfirmation: data.passwordConfirmation
    };

    return axios
      .post(`${BaseService.apiUrl()}/complete-reset-password`, requestData)
      .then(() => {
        Promise.resolve();
      })
      .catch(error => {
        let errorString = "";
        if (typeof error.response.data.errors[0].detail === "object") {
          errorString = error.response.data.errors[0].detail[0].detail;
        } else {
          errorString = error.response.data.errors[0].detail;
        }
        return Promise.reject(errorString);
      });
  },

  // To log out
  logout() {
    ls.remove(process.env.VUE_APP_LS_USER);
    ls.remove(process.env.VUE_APP_LS_SUBDOMAIN);
    ls.remove(process.env.VUE_APP_LS_PERMS);

    return new Promise(async (resolve, reject) => {
      const data = await axios.delete(
        `${BaseService.apiUrl()}/v1/sessions/logout`
      );

      if (data.status == 200) {
        store.dispatch("reset");
        store.dispatch("loggedOut");
        resolve(data);
      } else {
        reject(data);
      }
    });
  }
};
